if (!customElements.get('woodlas-header')) {
  customElements.define('woodlas-header', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.lastY = Math.max(0, window.scrollY);
      this.scrollDelta = 0;
      this.baseHeight = 0;
      const searchToggle = this.querySelector('.wl-header__search-toggle');
      this.setSearchOpen = open => {
        this.classList.toggle('is-search-open', open);
        searchToggle?.setAttribute('aria-expanded', String(open));
        if (open) {
          this.querySelectorAll('details[open]').forEach(detail => detail.open = false);
          this.setHidden(false);
          this.querySelector('.wl-header__search-form input')?.focus();
        }
      };
      searchToggle?.addEventListener('click', () => this.setSearchOpen(!this.classList.contains('is-search-open')), { signal });
      this.mobileQuery = matchMedia('(max-width: 1199px)');
      this.mobileQuery.addEventListener('change', () => this.setSearchOpen(false), { signal });
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        if (this.classList.contains('is-search-open')) {
          this.setSearchOpen(false);
          searchToggle?.focus();
          return;
        }
        const open = event.target.closest('details[open]');
        if (open) { open.open = false; open.querySelector('summary')?.focus(); }
      }, { signal });
      // Only sibling menus close each other; nested category accordions stay inside the mobile menu.
      this.querySelectorAll('details').forEach(detail => detail.addEventListener('toggle', () => {
        if (detail.open) {
          this.setSearchOpen(false);
          this.querySelectorAll('details[open]').forEach(other => {
            if (other !== detail && !other.contains(detail) && !detail.contains(other)) other.open = false;
          });
          this.setHidden(false);
        }
      }, { signal }));
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) {
          this.querySelectorAll('details[open]').forEach(detail => detail.open = false);
          this.setSearchOpen(false);
        }
      }, { signal });
      this.addEventListener('focusin', () => this.setHidden(false), { signal });
      window.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.updateScroll(); });
      }, { signal, passive: true });
      this.resizeObserver = new ResizeObserver(() => {
        const height = Math.ceil(this.getBoundingClientRect().height);
        if (height && height !== this.baseHeight) {
          this.baseHeight = height;
          document.documentElement.style.setProperty('--wl-premium-header-height', `${height}px`);
        }
      });
      this.resizeObserver.observe(this);
      this.querySelector('.wl-header__search-form')?.addEventListener('submit', event => {
        const input = event.currentTarget.querySelector('input[type="search"]');
        if (!input.value.trim()) { event.preventDefault(); input.focus(); }
      }, { signal });
      this.updateScroll();
      // Restored scroll positions reveal navigation immediately with the wooden finish.
      if (this.lastY > 8) this.setHidden(false);
    }
    updateScroll() {
      const y = Math.max(0, window.scrollY);
      const delta = y - this.lastY;
      this.lastY = y;
      this.classList.toggle('is-scrolled', y > 8);
      if (y <= 8 || this.dataset.autoHide === 'false') {
        this.scrollDelta = 0;
        this.setHidden(false);
        return;
      }
      const active = document.activeElement;
      const editingOrKeyboardFocus = this.contains(active) && (active.matches('input, textarea, select') || active.matches(':focus-visible'));
      if (editingOrKeyboardFocus || this.querySelector('details[open]') || this.classList.contains('is-search-open')) {
        this.scrollDelta = 0;
        this.setHidden(false);
        return;
      }
      if (delta && Math.sign(delta) !== Math.sign(this.scrollDelta)) this.scrollDelta = delta;
      else this.scrollDelta += delta;
      if (Math.abs(this.scrollDelta) >= 10) {
        this.setHidden(this.scrollDelta > 0);
        this.scrollDelta = 0;
      }
    }
    setHidden(hidden) {
      this.classList.toggle('is-hidden', hidden);
      this.inert = hidden;
      if (hidden) this.setAttribute('aria-hidden', 'true');
      else this.removeAttribute('aria-hidden');
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  });
}
